import { useEffect, useState } from "react";
import client from "@/lib/api";
import { toast } from "sonner";
import { MapPinned, ChevronDown, ChevronUp, CheckCircle2, XCircle, ShieldAlert, Loader2 } from "lucide-react";

// ADD-ON 34/35: Prior-location history alert + APCO-compliant review tracking.
// Additive panel — renders nothing when the address has no prior activity.
export const AddressIntelPanel = ({ incident, onChanged }) => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const addr = incident?.location?.address;

  useEffect(() => {
    if (!addr) return;
    client.get("/address-intel", { params: { address: addr } }).then((r) => setData(r.data)).catch(() => {});
  }, [addr]);

  if (!data || data.disabled) return null;
  const priorOnly = data.visits.filter((v) => v.cc !== incident.cc_number);
  if (!priorOnly.length) return null;
  const reviewed = incident.history_review;

  const review = async (action) => {
    setBusy(true);
    try {
      await client.post(`/incidents/${incident.id}/history-review`, { action });
      toast.success(`Location history marked: ${action.replace(/_/g, " ").toUpperCase()}`);
      onChanged?.();
    } catch { toast.error("Review failed"); }
    setBusy(false);
  };

  return (
    <div data-testid="address-intel-panel" className="border border-[#F59E0B]/50 bg-[#F59E0B]/5 rounded-sm">
      <button data-testid="address-intel-toggle" onClick={() => setOpen(!open)} className="w-full flex items-center gap-2 px-3 py-2 text-left">
        <MapPinned size={14} className="text-[#F59E0B] shrink-0" />
        <div className="min-w-0">
          <div className="font-head font-bold text-[11px] text-[#F59E0B] uppercase">
            Prior Location History Available — review for relevant responder-safety information
          </div>
          <div className="font-mono text-[9px] text-[#9CA3AF]">
            {data.master.aid} · {priorOnly.length} prior call(s) · last {priorOnly[0]?.ts ? new Date(priorOnly[0].ts).toLocaleDateString("en-GB") : "—"}
            {data.violent_recent.length > 0 && <span className="text-[#EF4444] font-bold"> · {data.violent_recent.length} VIOLENT/WEAPONS (12 mo)</span>}
            {data.premise_hazards.length > 0 && <span className="text-[#EF4444] font-bold"> · {data.premise_hazards.length} PREMISE HAZARD(S)</span>}
            {reviewed && <span className="text-[#22C55E]"> · REVIEWED by {reviewed.by}</span>}
          </div>
        </div>
        <span className="ml-auto text-[#F59E0B]">{open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}</span>
      </button>
      {open && (
        <div className="border-t border-[#F59E0B]/30 p-3 space-y-2.5">
          <div className="font-mono text-[9px] text-[#6B7280] uppercase">Total calls at this location (includes current event)</div>
          <div className="grid grid-cols-4 md:grid-cols-7 gap-1.5">
            {Object.entries(data.counts).map(([k, v]) => (
              <div key={k} className="bg-[#141518] border border-[#2C2D35] px-2 py-1 text-center rounded-sm">
                <div className="font-mono text-[8px] text-[#6B7280] uppercase">{k}</div>
                <div className="font-mono text-sm font-bold">{v}</div>
              </div>))}
            <div className="bg-[#141518] border border-[#2C2D35] px-2 py-1 text-center rounded-sm">
              <div className="font-mono text-[8px] text-[#6B7280] uppercase">All</div>
              <div className="font-mono text-sm font-bold text-[#22D3EE]">{data.total_calls}</div>
            </div>
          </div>
          {data.by_type.length > 0 && (
            <div className="font-mono text-[10px] text-[#9CA3AF]">
              TOP TYPES: {data.by_type.slice(0, 5).map(([t, n]) => `${t} (${n})`).join(" · ")}
            </div>)}
          {data.premise_hazards.length > 0 && (
            <div className="flex items-start gap-1.5 font-mono text-[10px] text-[#EF4444]">
              <ShieldAlert size={12} className="mt-0.5 shrink-0" />
              HAZARDS: {data.premise_hazards.map((h) => h.label || h.type || h).join(" · ")}
            </div>)}
          <div className="max-h-44 overflow-y-auto border border-[#1E1F24] rounded-sm">
            {priorOnly.slice(0, 15).map((v) => (
              <div key={v.cc} className="flex items-center gap-2 px-2 py-1.5 border-b border-[#1E1F24] text-[10px] font-mono" data-testid={`visit-${v.cc}`}>
                <span className="text-[#22D3EE] font-bold">{v.cc}</span>
                <span className="text-white truncate">{v.type}</span>
                <span className="text-[#6B7280]">P{v.priority}</span>
                <span className="text-[#6B7280] ml-auto shrink-0">{v.units?.slice(0, 3).join(",") || "—"} · {v.disposition} · {v.ts ? new Date(v.ts).toLocaleDateString("en-GB") : ""}</span>
              </div>))}
          </div>
          <div className="flex gap-2 flex-wrap">
            <button data-testid="history-mark-reviewed" onClick={() => review("reviewed")} disabled={busy}
              className="flex items-center gap-1 px-2.5 py-1.5 text-[9px] font-head font-bold bg-[#22C55E]/15 border border-[#22C55E]/50 text-[#4ade80] rounded-sm uppercase disabled:opacity-50">
              {busy ? <Loader2 size={11} className="animate-spin" /> : <CheckCircle2 size={11} />} Mark Reviewed
            </button>
            <button data-testid="history-not-relevant" onClick={() => review("not_relevant")} disabled={busy}
              className="flex items-center gap-1 px-2.5 py-1.5 text-[9px] font-head border border-[#2C2D35] text-[#9CA3AF] rounded-sm uppercase disabled:opacity-50">
              <XCircle size={11} /> Not Relevant
            </button>
            <button data-testid="history-supervisor-review" onClick={() => review("supervisor_review")} disabled={busy}
              className="flex items-center gap-1 px-2.5 py-1.5 text-[9px] font-head border border-[#F59E0B]/50 text-[#F59E0B] rounded-sm uppercase disabled:opacity-50">
              <ShieldAlert size={11} /> Supervisor Review
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
